import { Redo2, Undo2 } from "lucide-react";

import { Button, Textarea } from "@design-lab/shared";
import { useTokenManagement } from "../../model/tokenManagement";
import { CreditsHeader, ErrorMessage, getGenerateButtonContent } from "./shared";

export type RefinementFormMobileProps = {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onRefine: (prompt: string) => void;
  isLoading?: boolean;
  error?: string | undefined;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
};

export function RefinementFormMobile({
  prompt,
  onPromptChange,
  onRefine,
  isLoading = false,
  error = undefined,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo
}: RefinementFormMobileProps) {
  const { aiCreditsBalance } = useTokenManagement();
  const hasCredits = aiCreditsBalance > 0;

  const handleRefine = () => {
    if (prompt.trim()) {
      onRefine(prompt);
    }
  };

  return (
    <div className="flex flex-col">
      <CreditsHeader labelHtmlFor="refinement-prompt" helpTestId="refinement-help-trigger" />

      <div className="relative mx-0.5 mb-1 flex">
        <Textarea
          id="refinement-prompt"
          value={prompt}
          onChange={e => onPromptChange(e.target.value)}
          placeholder="e.g., A majestic lion wearing a crown, vintage comic book style"
          className="box-border w-full resize-none rounded-lg border border-gray-300 p-2 text-base font-normal placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
          rows={3}
          data-testid="refinement-prompt-textarea"
          autoFocus
        />
      </div>

      <div className="mx-0.5 mt-1 flex gap-2">
        {onUndo && onRedo && (
          <>
            <Button
              variant="icon"
              onPress={onUndo}
              isDisabled={!canUndo || isLoading}
              className="box-border flex h-11 items-center justify-center rounded-lg border border-blue-700 bg-white px-3 py-2 text-sm text-blue-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-gray-100 disabled:text-gray-400 disabled:opacity-50"
              data-testid="undo-button-mobile"
            >
              <Undo2 className="size-5" />
            </Button>
            <Button
              variant="icon"
              onPress={onRedo}
              isDisabled={!canRedo || isLoading}
              className="box-border flex h-11 items-center justify-center rounded-lg border border-blue-700 bg-white px-3 py-2 text-sm text-blue-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-gray-100 disabled:text-gray-400 disabled:opacity-50"
              data-testid="redo-button-mobile"
            >
              <Redo2 className="size-5" />
            </Button>
          </>
        )}
        <Button
          onPress={handleRefine}
          isDisabled={!prompt.trim() || isLoading || !hasCredits}
          className="bg-cta-gradient hover:bg-cta-gradient-hover flex h-11 flex-1 items-center justify-center gap-2 rounded-lg py-2 text-center text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-70"
          data-testid="refine-button"
        >
          {getGenerateButtonContent({
            isLoading,
            hasCredits,
            actionText: "Edit Artwork",
            loadingText: "Thinking..."
          })}
        </Button>
      </div>

      <ErrorMessage error={error} showNoCreditsMessage={!hasCredits} />
    </div>
  );
}
